// Create an outing. Multiple mutations, and the split is load-bearing: the owner
// outing_person row is nested in the same createOuting mutation — a deferred DB
// constraint requires the owner to exist at commit, and pdbq runs each mutation
// in one transaction — but the outing_route link and participant outing_person
// rows must be separate follow-up mutations, since nesting them fails RLS while
// the owner row is still invisible.
import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useSearchParams } from "wouter";
import { gqlRequest } from "../lib/gql";
import {
  CREATE_OUTING,
  CREATE_OUTING_PERSON,
  CREATE_OUTING_PHOTO,
  CREATE_OUTING_ROUTE,
  IMPORT_TRACK,
  ROUTE_REF,
  type CreateOutingResult,
  type ImportTrackResult,
  type PersonSummary,
  type RouteRefResult,
} from "../lib/queries";
import { X } from "lucide-react";
import { useAuth } from "../lib/auth";
import { uploadPhoto } from "../lib/photos";
import { parseGpx, type ParsedGpx } from "../lib/gpx";
import {
  ACTIVITIES,
  ACTIVITY_ICONS,
  CONDITIONS,
  formatDistance,
  OUTING_STATUSES,
  OUTING_VISIBILITIES,
  type Activity,
  type ConditionRating,
  type OutingStatus,
  type OutingVisibility,
} from "../lib/format";
import { useT } from "../lib/i18n";
import { LoginGate } from "../components/LoginGate";
import { PersonPicker } from "../components/PersonPicker";
import { PhotoPicker, type NewPhoto } from "../components/PhotoPicker";
import { RouteLinkPicker, type RouteLink } from "../components/RouteLinkPicker";
import { Chip, Field, Select, Stars, TextArea, TextInput } from "../components/ui";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

export function NewOutingPage() {
  return (
    <LoginGate action="gateAction.logOuting" next="/new">
      <OutingForm />
    </LoginGate>
  );
}

type Translate = ReturnType<typeof useT>["t"];

function friendlyError(err: unknown, t: Translate): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (/owner/i.test(msg)) return t("err.owner");
  if (/permission|denied|policy/i.test(msg)) return t("err.permission");
  if (/violates|constraint|invalid input/i.test(msg)) return t("err.rejected", { msg });
  return msg;
}

function OutingForm() {
  const { personId } = useAuth();
  const { t, labelizeT } = useT();
  const [, navigate] = useLocation();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [activity, setActivity] = useState<Activity>("HIKING");
  const [status, setStatus] = useState<OutingStatus>("COMPLETED");
  const [conditions, setConditions] = useState<ConditionRating | "">("");
  const [rating, setRating] = useState<number | null>(null);
  const [elapsedHours, setElapsedHours] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState<OutingVisibility>("PUBLIC");
  const [routeLinks, setRouteLinks] = useState<RouteLink[]>([]);
  const [gpx, setGpx] = useState<{ parsed: ParsedGpx; raw: string; fileName: string } | null>(null);
  const [gpxError, setGpxError] = useState<string | null>(null);
  const [photos, setPhotos] = useState<NewPhoto[]>([]);
  const [participants, setParticipants] = useState<PersonSummary[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // ?routeId= prefill (e.g. "Log outing" from a route page).
  const prefillId = params.get("routeId");
  const prefill = useQuery({
    queryKey: ["route-ref", prefillId],
    enabled: !!prefillId && routeLinks.length === 0,
    queryFn: () => gqlRequest<RouteRefResult>(ROUTE_REF, { id: prefillId }),
  });
  useEffect(() => {
    const route = prefill.data?.route;
    if (route) {
      setRouteLinks((prev) => (prev.length === 0 ? [{ route, style: "", result: "" }] : prev));
      if (route.activities.length) setActivity(route.activities[0]!);
    }
  }, [prefill.data]);

  async function onGpxSelect(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const raw = await file.text();
      setGpx({ parsed: parseGpx(raw), raw, fileName: file.name });
      setGpxError(null);
    } catch (err) {
      setGpx(null);
      setGpxError(err instanceof Error ? err.message : String(err));
    }
  }

  const planned = status === "PLANNED";

  const create = useMutation({
    mutationFn: async () => {
      // The track must exist before createOuting so its id can go in the
      // outing insert. importTrack is the only working track write.
      // A planned outing has nothing to rate or record yet, so conditions,
      // rating, time, track and photos are dropped regardless of form state.
      const input: Record<string, unknown> = {
        title: title.trim(),
        date,
        activity,
        status,
        conditions: planned ? null : conditions || null,
        rating: planned ? null : rating,
        elapsedSeconds: !planned && elapsedHours ? Math.round(Number(elapsedHours) * 3600) : null,
        description: description.trim() || null,
        visibility,
        outingPeopleByOutingId: { create: [{ personId, role: "OWNER" }] },
      };
      if (!planned && gpx) {
        const imported = await gqlRequest<ImportTrackResult>(IMPORT_TRACK, {
          geojson: gpx.parsed.geojson,
          pointTimes: gpx.parsed.pointTimes,
          sourceGpx: gpx.raw,
        });
        input.trackId = imported.importTrack.result.id;
      }
      const result = await gqlRequest<CreateOutingResult>(CREATE_OUTING, { input });
      const outingId = result.createOuting.outing.id;
      // List order becomes the sort value; style/result are per-route metadata.
      for (const [sort, link] of routeLinks.entries()) {
        await gqlRequest(CREATE_OUTING_ROUTE, {
          outingId,
          routeId: link.route.id,
          sort,
          style: link.style || null,
          result: link.result.trim() || null,
        });
      }
      // Participants are follow-up mutations for the same reason as the route
      // link: nesting them in createOuting fails RLS — pdbq inserts nested
      // rows in one statement, so the owner row isn't visible yet when the
      // participant row's insert policy runs.
      for (const p of participants) {
        await gqlRequest(CREATE_OUTING_PERSON, { outingId, personId: p.id });
      }
      // Photos after the outing exists: upload the bytes to photod, then link
      // the storage key with a row. Selection order becomes sort order.
      for (const [index, photo] of (planned ? [] : photos).entries()) {
        const storageKey = await uploadPhoto(photo.file);
        await gqlRequest(CREATE_OUTING_PHOTO, {
          outingId,
          storageKey,
          caption: photo.caption.trim() || null,
          sort: index,
        });
      }
      return outingId;
    },
    // onSettled, not onSuccess: the outing may exist even when a follow-up
    // route/participant mutation failed, so the feed must refetch either way.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["feed"] });
    },
    onSuccess: (outingId) => {
      navigate(`/outing/${outingId}`);
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!title.trim()) errors.title = t("newOuting.errTitle");
    if (!date) errors.date = t("newOuting.errDate");
    else if (!planned && date > new Date().toISOString().slice(0, 10)) errors.date = t("newOuting.errFuture");
    if (!planned && elapsedHours && (!Number.isFinite(Number(elapsedHours)) || Number(elapsedHours) <= 0))
      errors.elapsed = t("newOuting.errElapsed");
    setFieldErrors(errors);
    if (Object.keys(errors).length === 0) create.mutate();
  }

  return (
    <div className="mx-auto max-w-xl p-4">
      <h1 className="mb-5 text-2xl font-semibold tracking-tight text-foreground">{t("newOuting.title")}</h1>
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label={t("form.title")} error={fieldErrors.title}>
          <TextInput value={title} placeholder={t("newOuting.titlePlaceholder")} onChange={(e) => setTitle(e.target.value)} />
        </Field>

        <Field label={t("form.status")}>
          <div className="flex flex-wrap gap-2">
            {OUTING_STATUSES.map((s) => (
              <Chip key={s} active={status === s} onClick={() => setStatus(s)}>
                {labelizeT(s)}
              </Chip>
            ))}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{t("newOuting.plannedHint")}</p>
        </Field>

        <Field label={t("newOuting.routesOptional")}>
          <RouteLinkPicker
            links={routeLinks}
            onChange={setRouteLinks}
            onAddRoute={(r) => {
              if (r.activities.length) setActivity(r.activities[0]!);
            }}
          />
        </Field>

        {!planned && (
          <Field label={t("newOuting.gpxOptional")} error={gpxError ?? undefined}>
            {gpx ? (
              <div className="flex items-center justify-between rounded-xl border bg-accent px-3 py-2">
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-foreground">{gpx.fileName}</span>
                  <span className="block text-xs text-muted-foreground">
                    {t("gpx.stats", { count: gpx.parsed.pointCount, distance: formatDistance(gpx.parsed.distanceM) })}
                  </span>
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setGpx(null)}
                  className="ml-2 shrink-0 text-muted-foreground hover:text-destructive"
                  aria-label={t("gpx.removeAria")}
                >
                  <X aria-hidden className="size-4" />
                </Button>
              </div>
            ) : (
              <input
                type="file"
                accept=".gpx"
                onChange={onGpxSelect}
                className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-lg file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-primary hover:file:bg-primary/20"
              />
            )}
          </Field>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label={t("form.date")} error={fieldErrors.date}>
            <TextInput type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          {!planned && (
            <Field label={t("newOuting.timeHours")} error={fieldErrors.elapsed}>
              <TextInput
                type="number"
                step="0.5"
                min="0"
                placeholder="e.g. 5.5"
                value={elapsedHours}
                onChange={(e) => setElapsedHours(e.target.value)}
              />
            </Field>
          )}
        </div>

        <Field label={t("form.activity")}>
          <div className="flex flex-wrap gap-2">
            {ACTIVITIES.map((a) => {
              const Icon = ACTIVITY_ICONS[a];
              return (
                <Chip key={a} active={activity === a} onClick={() => setActivity(a)}>
                  <span className="inline-flex items-center gap-1.5">
                    <Icon aria-hidden className="size-3.5" />
                    {labelizeT(a)}
                  </span>
                </Chip>
              );
            })}
          </div>
        </Field>

        {!planned && (
          <div className="grid grid-cols-2 items-end gap-3">
            <Field label={t("form.conditions")}>
              <Select value={conditions} onChange={(e) => setConditions(e.target.value as ConditionRating | "")}>
                <option value="">{t("common.notSpecified")}</option>
                {CONDITIONS.map((c) => (
                  <option key={c} value={c}>
                    {labelizeT(c)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t("form.rating")}>
              <Stars rating={rating} onChange={setRating} />
            </Field>
          </div>
        )}

        <Field label={t("form.notes")}>
          <TextArea
            rows={4}
            value={description}
            placeholder={t("newOuting.notesPlaceholder")}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>

        {!planned && (
          <Field label={t("newOuting.photosOptional")}>
            <PhotoPicker photos={photos} onChange={setPhotos} />
          </Field>
        )}

        <Field label={t("newOuting.participantsOptional")}>
          <PersonPicker
            selected={participants}
            onChange={setParticipants}
            excludeIds={personId ? [personId] : []}
          />
        </Field>

        <Field label={t("form.visibility")}>
          <Select value={visibility} onChange={(e) => setVisibility(e.target.value as OutingVisibility)}>
            {OUTING_VISIBILITIES.map((v) => (
              <option key={v} value={v}>
                {labelizeT(v)}
              </option>
            ))}
          </Select>
          <p className="mt-1 text-xs text-muted-foreground">{t("newOuting.followersHint")}</p>
        </Field>

        <p className="text-xs text-muted-foreground">{t("newOuting.ownerNote")}</p>

        {create.error != null && (
          <Alert variant="destructive">
            <AlertDescription>{friendlyError(create.error, t)}</AlertDescription>
          </Alert>
        )}

        <Button type="submit" disabled={create.isPending} className="w-full py-2.5">
          {create.isPending ? t("common.saving") : t("newOuting.submit")}
        </Button>
      </form>
    </div>
  );
}
